import { createContext, useContext, useState, useMemo, type ReactNode } from 'react';
import type { Dayjs } from 'dayjs';

/**
 * A shared, app-wide date range used by the topbar date selector and any page
 * that wants to scope its data to a period (currently the Transactions page).
 *
 * `null` (or a tuple with a `null` bound) means "no date filter" — pages should
 * show all data. Defaulting to `null` avoids hiding freshly-uploaded records on
 * first load.
 */
export type DateRangeValue = [Dayjs | null, Dayjs | null] | null;

interface DateRangeContextType {
  dateRange: DateRangeValue;
  setDateRange: (range: DateRangeValue) => void;
}

const DateRangeContext = createContext<DateRangeContextType | undefined>(undefined);

export function DateRangeProvider({ children }: { children: ReactNode }) {
  const [dateRange, setDateRange] = useState<DateRangeValue>(null);
  const value = useMemo(() => ({ dateRange, setDateRange }), [dateRange]);
  return <DateRangeContext.Provider value={value}>{children}</DateRangeContext.Provider>;
}

export function useDateRange(): DateRangeContextType {
  const ctx = useContext(DateRangeContext);
  if (!ctx) {
    throw new Error('useDateRange must be used within a DateRangeProvider');
  }
  return ctx;
}
